"use client";
import React, { useState, useRef, useEffect, memo } from "react";
import PropTypes from "prop-types";
import Image from "next/image";
import { toast } from "react-toastify";
import { useDeleteMediaByIdForLocationMutation } from "@/lib/redux/api/locationApi";
import DeletePopup from "@/components/Popups/DeletePopup";
import Pdf from "@/assets/images/pdf.jpeg";
import Word from "@/assets/images/word.jpeg";
import getNestedError from "@/lib/utils/getNestedError";
import {
  useDeleteChildImageMutation,
  useDeleteClientImageMutation,
} from "@/lib/redux/api/clientsApi";

const MAX_FILE_SIZE_MB = 10; // Individual file size limit
const MAX_TOTAL_SIZE_MB = 10; // Total size limit for all files
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const ALLOWED_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png"];

const FileInputImages = ({
  label,
  name,
  register,
  errors,
  setValue,
  clearErrors,
  customType = "image",
  buttonText,
  isMulti,
  existingMedia = [],
  accept = "*/*",
  deleteType = "normal",
  onDelete,
  value,
  onChange,
  labelColor = null,
  labelPosition = "top",
  fieldHeight = "medium",
}) => {
  // Use refs to store the actual file data to prevent re-renders
  const selectedFilesRef = useRef([]);
  const preExistingFilesRef = useRef([]);
  // State only for UI rendering
  const [, forceUpdate] = useState({});
  const [fileErrors, setFileErrors] = useState([]);
  const fileInputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [itemToBeDeleted, setItemToBeDeleted] = useState(null);
  // Memoize error message
  const errorMessage = React.useMemo(
    () => getNestedError(errors, name),
    [errors, name]
  );

  // Initialize preExistingFiles when existingMedia changes
  useEffect(() => {
    if (existingMedia && existingMedia.length > 0) {
      // Special handling for document types
      if (customType === "doctype") {
        // For documents, always update preExistingFilesRef
        preExistingFilesRef.current = [...existingMedia];
        forceUpdate({});
        return;
      }

      // Don't filter by media_type, accept all media types
      preExistingFilesRef.current = existingMedia;
      // Force a re-render without changing state that would cause parent re-renders
      forceUpdate({});
    }
  }, [existingMedia, customType]);

  // If value is provided externally, sync with our refs
  useEffect(() => {
    if (value) {
      if (value.newFiles) {
        selectedFilesRef.current = Array.from(value.newFiles);
      }
      if (value.existingMedia) {
        preExistingFilesRef.current = value.existingMedia;
      }
      // Force a re-render
      forceUpdate({});
    }
  }, [value]);

  const calculateTotalSize = (files) =>
    files.reduce((total, file) => total + (file.size || 0), 0);

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const newFileErrors = [];
    const isImageField = customType === "image";
    const validFiles = files.filter((file) => {
      if (!isImageField) {
        return true;
      }
      const fileType = (file.type || "").toLowerCase();
      const fileName = (file.name || "").toLowerCase();
      const hasAllowedType = ALLOWED_IMAGE_TYPES.includes(fileType);
      const hasAllowedExtension = ALLOWED_IMAGE_EXTENSIONS.some((ext) =>
        fileName.endsWith(ext)
      );
      return hasAllowedType || hasAllowedExtension;
    });

    const invalidTypeFiles = files.filter((file) => !validFiles.includes(file));
    if (invalidTypeFiles.length > 0) {
      newFileErrors.push(
        "Only JPG, JPEG, and PNG files are allowed."
      );
    }
    // Check individual file sizes first
    let totalNewSize = 0;
    const validSizedFiles = [];
    for (const file of validFiles) {
      const fileSizeInMB = file.size / (1024 * 1024);
      totalNewSize += file.size;
      if (fileSizeInMB > MAX_FILE_SIZE_MB) {
        newFileErrors.push(
          `${file.name} (${fileSizeInMB.toFixed(
            2
          )}MB) exceeds the ${MAX_FILE_SIZE_MB}MB size limit`
        );
      } else {
        validSizedFiles.push(file);
      }
    }
    // Check total size including existing files
    const currentTotalSize =
      calculateTotalSize(selectedFilesRef.current) +
      calculateTotalSize(preExistingFilesRef.current);
    const combinedSizeMB = (currentTotalSize + totalNewSize) / (1024 * 1024);
    if (combinedSizeMB > MAX_TOTAL_SIZE_MB) {
      setFileErrors([
        `Total file size (${combinedSizeMB.toFixed(
          2
        )}MB) exceeds ${MAX_TOTAL_SIZE_MB}MB limit.`,
      ]);
      event.target.value = "";
      return;
    }
    setFileErrors(newFileErrors);
    // Only proceed if we have valid files and no size errors
    if (validSizedFiles.length === 0) {
      if (newFileErrors.length > 0) {
        toast.error(newFileErrors[0]);
      }
      event.target.value = "";
      return;
    }
    // Handle file selection
    if (isMulti === "false") {
      selectedFilesRef.current = validSizedFiles.slice(0, 1);
      preExistingFilesRef.current = [];
    } else {
      selectedFilesRef.current = [
        ...selectedFilesRef.current,
        ...validSizedFiles,
      ];
    }
    // Update the form value
    const newValue = {
      newFiles: selectedFilesRef.current,
      existingMedia: preExistingFilesRef.current,
    };
    // Use the onChange prop if provided, otherwise use setValue
    if (onChange) {
      onChange(newValue);
    } else {
      setValue(name, newValue, { shouldDirty: true });
    }
    clearErrors(name);
    setFileErrors([]);
    // Force a re-render
    forceUpdate({});
    event.target.value = "";
  };

  const [deleteImage, { isLoading }] = useDeleteMediaByIdForLocationMutation();
  const [deleteChildImage, { isLoading: isDeletingChildImage }] =
    useDeleteChildImageMutation();
  const [deleteParentImage, { isLoading: isDeletingParentImage }] =
    useDeleteClientImageMutation();

  const handleDelete = async (type) => {
    
    try {
      switch (type) {
        case "media": {
          const res = await deleteImage(itemToBeDeleted?.id).unwrap();
          preExistingFilesRef.current = preExistingFilesRef.current.filter(
            (file) => file.id !== itemToBeDeleted?.id
          );
          toast.success(res?.message);
          break;
        }
        case "deletechild": {
          const childRes = await deleteChildImage(itemToBeDeleted?.id).unwrap();
          preExistingFilesRef.current = preExistingFilesRef.current.filter(
            (file) => file.id !== itemToBeDeleted?.id
          );
          toast.success(childRes?.message);
          break;
        }
        case "deleteParentImage": {
          const parentRes = await deleteParentImage(
            itemToBeDeleted?.id
          ).unwrap();
          preExistingFilesRef.current = preExistingFilesRef.current.filter(
            (file) => file.id !== itemToBeDeleted?.id
          );
          onDelete && onDelete(preExistingFilesRef.current);
          toast.success(parentRes?.message);
          break;
        }
        default: {
          preExistingFilesRef.current = preExistingFilesRef.current.filter(
            (file) => file.id !== itemToBeDeleted?.id
          );
          toast.success("File removed successfully");
        }
      }
      const newValue = {
        newFiles: selectedFilesRef.current,
        existingMedia: preExistingFilesRef.current,
      };
      if (onChange) {
        onChange(newValue);
      } else {
        setValue(name, newValue, { shouldDirty: true });
      }
      forceUpdate({});
    } catch (error) {
            toast.error(error?.data?.error || "Failed to delete file");
    }
    setIsOpen(false);
    setItemToBeDeleted(null);
  };

  const removeFile = (index, isPreExisting = false) => {
    clearErrors(name);
    setFileErrors([]);
    if (isPreExisting) {
      const itemToDelete = preExistingFilesRef.current.find(
        (_, i) => i === index
      );
      setItemToBeDeleted(itemToDelete);
      setIsOpen(true);
    } else {
      selectedFilesRef.current = selectedFilesRef.current.filter(
        (_, i) => i !== index
      );
      // Update the form value
      const newValue = {
        newFiles: selectedFilesRef.current,
        existingMedia: preExistingFilesRef.current,
      };
      // Use the onChange prop if provided, otherwise use setValue
      if (onChange) {
        onChange(newValue);
      } else {
        setValue(name, newValue, { shouldDirty: true });
      }
      // Force a re-render
      forceUpdate({});
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Memoize this function to prevent unnecessary re-renders
  // Improve the getFileTypeIcon function
  const getFileTypeIcon = React.useCallback((file) => {
    // Check if it's a PDF file
    if (
      file.type?.includes("pdf") ||
      file.media_path?.toLowerCase().includes("pdf") ||
      file.file_extension === "pdf" ||
      file.document_path?.toLowerCase().includes("pdf")
    ) {
      return Pdf;
    }
    // Check if it's a Word document
    else if (
      file.type?.includes("word") ||
      file.type?.includes("msword") ||
      file.type?.includes("openxmlformats-officedocument") ||
      file.media_path?.toLowerCase().match(/\.(doc|docx)$/) ||
      file.file_extension === "doc" ||
      file.file_extension === "docx" ||
      file.document_path?.toLowerCase().match(/\.(doc|docx)$/)
    ) {
      return Word;
    }
    // Default to PDF for any document type
    if (file.media_type === "Document") {
      return Pdf;
    }
    // For other file types
    return Pdf;
  }, []);

  // Improve the isImageFile function to correctly identify documents
  const isImageFile = React.useCallback((file) => {

    // For document type components, always return false to use document icons
    if (customType === "doctype") {
      return false;
    }

    // If media_type is explicitly set to Document, it's not an image
    if (file.media_type === "Document") {
      return false;
    }
    // Check file type for uploaded files
    if (file.type) {
      return file.type.startsWith("image/");
    }
    // For existing files, check the path
    const path = file.media_path?.toLowerCase() || "";
    // If path contains doc, docx, or pdf, it's not an image
    if (path.includes("pdf") || path.match(/\.(doc|docx)$/)) {
      return false;
    }
    // Otherwise check for image extensions
    return path.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/);
  }, [customType]);

  // Register the input only once
  useEffect(() => {
    register(name);
  }, [register, name]);

  // Get field height classes based on fieldHeight - must match InputField exactly
  const getFieldHeightClasses = () => {
    switch (fieldHeight) {
      case "small":
        return "py-1.5 px-3 text-sm min-h-[36px]";
      case "large":
        return "py-3.5 px-4 text-base min-h-[56px]";
      case "medium":
      default:
        return "py-2 sm:py-2.5 px-4 text-sm sm:text-base min-h-[46px]";
    }
  };

  // Get label classes based on fieldHeight
  const getLabelClasses = () => {
    switch (fieldHeight) {
      case "small":
        return "text-xs sm:text-sm mb-2";
      case "large":
        return "text-base sm:text-lg mb-3";
      case "medium":
      default:
        return "text-sm sm:text-base mb-2 sm:mb-2.5";
    }
  };

  const heightClasses = getFieldHeightClasses();
  const labelClasses = getLabelClasses();
  const labelStyle = labelColor ? { color: labelColor } : { color: '#374151' }; // default text-gray-700

  const hasFiles = selectedFilesRef.current.length > 0 || preExistingFilesRef.current.length > 0;
  const displayText = hasFiles
    ? `${selectedFilesRef.current.length + preExistingFilesRef.current.length} file(s) selected`
    : (buttonText || "Browse files");

  return (
    <div className="">
      {label && (
        <label 
          className={`block ${labelClasses}`}
          style={labelStyle}
        >
          {label}
        </label>
      )}
      <div className="relative">
        <input
          type="file"
          id={name}
          accept={accept}
          multiple={isMulti === "true" ? true : false}
          onChange={handleFileChange}
          ref={fileInputRef}
          className="absolute -z-10 opacity-0"
        />
        {/* Custom button styled to match InputField exactly */}
        <button
          type="button"
          onClick={triggerFileInput}
          className={`${heightClasses} text-left text-black border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5D45EF] cursor-pointer flex items-center justify-between gap-4 ${errorMessage ? "border-red-500 focus:ring-red-500/50" : ""}`}
        >
          <span className={hasFiles ? "text-black" : "text-gray-400"}>
            {displayText}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-gray-400 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
        </button>
      </div>
      {errorMessage && (
        <p className="text-red-500 text-sm mt-2.5">{errorMessage}</p>
      )}
      {/* Error Messages */}
      {fileErrors.length > 0 && (
        <div className="mt-2 text-red-500 text-xs">
          {fileErrors.map((error, index) => (
            <p key={index}>{error}</p>
          ))}
        </div>
      )}
      {/* Preview Section */}
      {(preExistingFilesRef.current.length > 0 ||
        selectedFilesRef.current.length > 0) && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Selected files:
            </p>
            <div className="flex flex-wrap gap-4">
              {/* Pre-existing files */}
              {preExistingFilesRef.current.map((media, index) => (
                <div
                  key={`existing-${media.id || index}`}
                  className="relative w-24 h-24"
                >
                  {isImageFile(media) ? (
                    <Image
                      src={media.media_path}
                      alt={media.file_name || "Image"}
                      layout="fill"
                      objectFit="cover"
                      className="rounded-lg border border-gray-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image
                        src={getFileTypeIcon(media)}
                        alt={media.file_name || "Document"}
                        width={80}
                        height={80}
                        objectFit="contain"
                        className="rounded-lg border border-gray-300 p-2"
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removeFile(index, true)}
                    className="absolute top-1 right-1 bg-red-500 text-white text-xs px-1 rounded"
                  >
                    ×
                  </button>
                </div>
              ))}
              {/* Newly selected files */}
              {selectedFilesRef.current.map((file, index) => (
                <div key={`new-${index}`} className="relative w-24 h-24">
                  {isImageFile(file) ? (
                    <Image
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      layout="fill"
                      objectFit="cover"
                      className="rounded-lg border border-gray-300"
                    />
                  ) : (
                    <Image
                      src={getFileTypeIcon(file) || Pdf}
                      alt={file.name}
                      layout="fill"
                      objectFit="contain"
                      className="rounded-lg border border-gray-300 p-2"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="absolute top-1 right-1 bg-red-500 text-white text-xs px-1 rounded"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      {/* Popup for Confirmation */}
      <DeletePopup
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onConfirm={() => handleDelete(deleteType)}
        message="You are about to remove this file. This action cannot be undone."
        isLoading={isLoading || isDeletingChildImage || isDeletingParentImage}
        isDisabled={isLoading || isDeletingChildImage || isDeletingParentImage}
      />
    </div>
  );
};

FileInputImages.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired,
  accept: PropTypes.string,
  existingMedia: PropTypes.array,
  customType: PropTypes.string,
  isMulti: PropTypes.string,
  deleteType: PropTypes.string,
  onDelete: PropTypes.func,
  buttonText: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  labelColor: PropTypes.string,
  labelPosition: PropTypes.string,
  fieldHeight: PropTypes.string,
};

// Custom equality function for memo
function arePropsEqual(prevProps, nextProps) {
  // Always check if existingMedia has changed
  if (JSON.stringify(prevProps.existingMedia) !== JSON.stringify(nextProps.existingMedia)) {
    return false; // Force re-render if existingMedia changed
  }
  
  // For document types, we need additional checks
  if (prevProps.customType === "doctype" || nextProps.customType === "doctype") {
    // Compare existingMedia arrays for document types
    const prevExistingMedia = JSON.stringify(prevProps.existingMedia || []);
    const nextExistingMedia = JSON.stringify(nextProps.existingMedia || []);
    if (prevExistingMedia !== nextExistingMedia) {
      return false; // Force re-render if existingMedia changed for documents
    }
  }
  
  // For images, use the original comparison logic to prevent flickering
  return (
    prevProps.name === nextProps.name &&
    prevProps.isMulti === nextProps.isMulti &&
    prevProps.errors[prevProps.name] === nextProps.errors[nextProps.name] &&
    prevProps.value === nextProps.value
  );
}

// Export as memoized component with custom equality function
export default memo(FileInputImages, arePropsEqual);

